import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ComparacaoProtocolo } from '../comparacao/ComparacaoProtocolo'
import { CondicaoCatalogo } from '../../formularios/core/condicao/catalogo'
import { ORDEM_RISCO, RISCOS } from '../../../../components/TriagemRisco/riscoTriagem'
import { TriagemRiscoBadge } from '../../../../components/TriagemRisco/TriagemRiscoBadge'
import { ErroProtocolo, descreverErroItem, extrairErrosProtocolo } from '../core/errosProtocolo'
import { ImpedimentoAprovacao, ProtocoloDetalhe } from '../core/_models'
import { aprovarProtocolo, compararVersoes, obterPreviaAprovacao } from '../core/_requests'
import { Button } from '../../../../components/Button/Button'
import { useModal } from '../../../../components/Modal/ModalContext'

type Props = {
  protocolo: ProtocoloDetalhe
  catalogo?: CondicaoCatalogo
  onAprovado?: () => void
}

const TEXTO_IMPEDIMENTO: Record<ImpedimentoAprovacao, string> = {
  SEM_PERMISSAO_EMPRESA:
    'Você não tem permissão para aprovar protocolos nesta empresa. A aprovação será recusada.',
  APROVADOR_SEM_CRM_NA_EMPRESA:
    'Para aprovar é preciso ser profissional com CRM e vínculo ativo nesta empresa. A aprovação será recusada.',
  NAO_RASCUNHO: 'Esta versão não está em rascunho e não pode ser aprovada.',
}

/**
 * Confirmação da aprovação (T7a AC5): resumo, diff contra a vigente, quem será gravado como
 * aprovador e os avisos de substituição e de bloqueio do formulário.
 *
 * Conteúdo do `showModal` (título e fechamento ficam com o `ModalContext`).
 */
export const AprovarProtocoloModal: FC<Props> = ({ protocolo, catalogo, onAprovado }) => {
  const queryClient = useQueryClient()
  const { closeModal } = useModal()
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<ErroProtocolo | null>(null)
  const alertaRef = useRef<HTMLDivElement>(null)

  const { data: previa } = useQuery(
    ['protocolos-triagem', 'previa', protocolo.id],
    () => obterPreviaAprovacao(protocolo.id),
    { retry: false }
  )
  const { data: comparacao } = useQuery(
    ['protocolos-triagem', 'comparacao', protocolo.id, 'vigente'],
    () => compararVersoes(protocolo.id),
    { retry: false }
  )

  useEffect(() => {
    if (erro) {
      alertaRef.current?.focus()
    }
  }, [erro])

  const contagemPorCor = useMemo(() => {
    const contagem: Record<string, number> = {}
    ;(protocolo.regras ?? []).forEach((regra) => {
      contagem[regra.cor] = (contagem[regra.cor] ?? 0) + 1
    })
    return contagem
  }, [protocolo.regras])

  const confirmar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      await aprovarProtocolo(protocolo.id)
      await queryClient.invalidateQueries(['protocolos-triagem'])
      onAprovado?.()
      closeModal()
    } catch (excecao) {
      setErro(extrairErrosProtocolo(excecao))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <dl className='row'>
        <dt className='col-sm-4'>Versão</dt>
        <dd className='col-sm-8'>
          {protocolo.versao} — {protocolo.nome}
        </dd>
        <dt className='col-sm-4'>Formulário vinculado</dt>
        <dd className='col-sm-8'>{protocolo.formulario?.nome ?? '—'}</dd>
        <dt className='col-sm-4'>Regras por risco</dt>
        <dd className='col-sm-8'>
          {ORDEM_RISCO.filter((cor) => contagemPorCor[cor]).map((cor) => (
            <span key={cor} className='me-3 d-inline-flex align-items-center gap-2'>
              <TriagemRiscoBadge cor={cor} />
              <span>
                {contagemPorCor[cor]} regra(s) {RISCOS[cor].nome.toLowerCase()}
              </span>
            </span>
          ))}
          {Object.keys(contagemPorCor).length === 0 && <span>Nenhuma regra</span>}
        </dd>
        <dt className='col-sm-4'>Será gravado como aprovador</dt>
        <dd className='col-sm-8'>
          {previa?.nomeAprovador && previa?.crmAprovador
            ? `${previa.nomeAprovador} — ${previa.crmAprovador}`
            : '—'}
        </dd>
      </dl>

      {previa?.impedimento && (
        <div className='alert alert-warning' role='alert'>
          {TEXTO_IMPEDIMENTO[previa.impedimento]}
        </div>
      )}

      <ul className='mb-6'>
        {previa?.vigenteAtual && (
          <li>A versão vigente {previa.vigenteAtual.versao} será substituída.</li>
        )}
        <li>
          O formulário {protocolo.formulario?.nome ?? 'vinculado'} ficará bloqueado para edição.
        </li>
      </ul>

      <ComparacaoProtocolo
        comparacao={comparacao}
        catalogo={catalogo}
        titulo='Diferenças para a versão vigente'
      />

      {erro && (
        <div className='alert alert-danger mt-6' role='alert' tabIndex={-1} ref={alertaRef}>
          <p className='mb-2'>{erro.message}</p>
          {erro.erros.length > 0 && (
            <ul className='mb-0'>
              {erro.erros.map((item, indice) => (
                <li key={`${item.codigo}-${indice}`}>{descreverErroItem(item)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className='d-flex justify-content-end gap-2 mt-6'>
        <Button className='btn-light' onClick={closeModal} disabled={enviando}>
          Cancelar
        </Button>
        <Button className='btn-primary' onClick={confirmar} disabled={enviando}>
          {enviando ? 'Aprovando...' : 'Aprovar versão'}
        </Button>
      </div>
    </>
  )
}
